import React from 'react';
import Icon from '../../../components/AppIcon';

const PredictionTimelinePanel = ({ predictions, anomalies }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-blue-500'
    };
    return colors?.[severity] || 'bg-gray-500';
  };

  const getTimeframeHours = (timeframe) => {
    if (timeframe?.includes('24')) return 24;
    if (timeframe?.includes('48')) return 48;
    if (timeframe?.includes('12')) return 12;
    return 6;
  };

  const timelineItems = [
    ...predictions?.map(p => ({
      type: 'prediction',
      severity: p?.severity,
      title: p?.issue,
      timeframe: p?.timeframe,
      hours: getTimeframeHours(p?.timeframe),
      confidence: p?.confidence
    })),
    ...anomalies?.map(a => ({
      type: 'anomaly',
      severity: a?.severity,
      title: a?.message,
      timeframe: 'Now',
      hours: 0
    }))
  ]?.sort((a, b) => a?.hours - b?.hours);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Clock" className="w-5 h-5 text-purple-600" />
          Interactive Prediction Timeline
        </h3>
        <p className="text-sm text-gray-600 mt-1">Forecasted issues with severity levels and impact assessments</p>
      </div>
      {/* Timeline */}
      {timelineItems?.length > 0 ? (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* Timeline Items */}
          <div className="space-y-6">
            {timelineItems?.map((item, index) => (
              <div key={index} className="relative pl-12">
                {/* Timeline Dot */}
                <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  getSeverityColor(item?.severity)
                }`}>
                  <Icon
                    name={item?.type === 'anomaly' ? 'Zap' : 'AlertTriangle'}
                    className="w-4 h-4 text-white"
                  />
                </div>

                {/* Content */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                          getSeverityColor(item?.severity)
                        }`}>
                          {item?.severity?.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-600">{item?.timeframe}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900">{item?.title}</h4>
                    </div>
                    {item?.confidence && (
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Confidence</div>
                        <div className="text-lg font-bold text-purple-600">{item?.confidence}%</div>
                      </div>
                    )}
                  </div>

                  {/* Type Badge */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      item?.type === 'anomaly' ?'bg-yellow-100 text-yellow-800' :'bg-purple-100 text-purple-800'
                    }`}>
                      {item?.type === 'anomaly' ? 'Real-time Anomaly' : 'AI Prediction'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Icon name="Clock" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Data</h3>
          <p className="text-gray-600">Run predictive analysis to generate timeline</p>
        </div>
      )}
      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="text-sm font-medium text-gray-700 mb-3">Severity Levels</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-sm text-gray-700">Critical</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-sm text-gray-700">High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-sm text-gray-700">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm text-gray-700">Low</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionTimelinePanel;
